import { useState } from 'react';
import { Car, ArrowLeft, CheckCircle, Shield, Calendar, Gauge, Settings, Fuel, ChevronLeft, ChevronRight } from 'lucide-react';
import { IslamicSymbol } from './ui/islamic-symbol';
import { NavigationBar } from "./shared/Navigation";
import { Footer } from "./shared/Footer";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";

import { useLanguage } from '../contexts/LanguageContext';
import type { Car as CarType } from "./Listings";


function ImageGallery({ images }: { images?: string[] }) {
  const [currentImage, setCurrentImage] = useState(0);
  
  // Use actual images if available, otherwise show placeholder
  const displayImages = images && images.length > 0 ? images : [];
  const hasImages = displayImages.length > 0;

  const nextImage = () => {
    if (hasImages) {
      setCurrentImage((prev) => (prev + 1) % displayImages.length);
    }
  };

  const prevImage = () => {
    if (hasImages) {
      setCurrentImage((prev) => (prev - 1 + displayImages.length) % displayImages.length);
    }
  };

  return (
    <Card className="overflow-hidden w-full lg:max-w-[880px] mx-auto">
      <div className="relative">
        {hasImages ? (
          // Show actual car images
          <div className="w-full h-80 sm:h-96 lg:w-[880px] lg:h-[480px] relative mx-auto">
            <img
              src={displayImages[currentImage]}
              alt={`${currentImage + 1} of ${displayImages.length}`}
              className="w-full h-full object-cover"
            />
            
            {displayImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90"
                  onClick={prevImage}
                >
                  <ChevronLeft size={20} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90"
                  onClick={nextImage}
                >
                  <ChevronRight size={20} />
                </Button>
                
                <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                  {currentImage + 1} / {displayImages.length}
                </div>
              </>
            )}
          </div>
        ) : (
          // Show placeholder when no images
          <div className="w-full h-80 sm:h-96 lg:w-[880px] lg:h-[480px] bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 flex items-center justify-center mx-auto">
            <Car size={80} className="text-[#f91bdc]/60" />
          </div>
        )}
      </div>
      
      {hasImages && displayImages.length > 1 && (
        <div className="p-4">
          <div className="flex space-x-2 overflow-x-auto">
            {displayImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`flex-shrink-0 w-16 h-12 rounded border-2 transition-all duration-200 overflow-hidden ${
                  currentImage === index 
                    ? 'border-[#f91bdc]' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// Basic car info and payment details for the sidebar
function CarBasicInfo({ car, onCarBuy }: { car: CarType; onCarBuy: (car: CarType) => void }) {
  const { t } = useLanguage();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 mb-3 leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
          {car.year} {car.make} {car.model}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="border-[#f91bdc] text-[#f91bdc] px-3 py-1">
            {car.type}
          </Badge>
          <span className="text-gray-600 text-sm">{t('detailedAd.soldBy')} {car.dealer}</span>
          <span className="text-gray-500 text-sm">• {car.location.city}, {car.location.stateProvince}</span>
        </div>
      </div>

      <Card className="border-[#f91bdc]/20 bg-gradient-to-br from-[#f91bdc]/5 to-[#f91bdc]/10">
        <CardContent className="p-5">
          <div className="space-y-4">
            {/* Monthly Payment - Primary highlight */}
            <div className="text-center p-4 bg-white rounded-lg border border-[#f91bdc]/20 shadow-sm">
              <h3 className="text-sm font-medium text-gray-600 mb-1">{t('car.monthlyPayment')}</h3>
              <div className="text-3xl lg:text-4xl font-bold text-[#f91bdc] mb-1">
                ${car.monthlyPayment}
              </div>
              <p className="text-xs text-gray-500">{t('detailedAd.perMonth')} • {car.termMonths} {t('car.months')}</p>
            </div>
            
            {/* Total Price / MSRP - Secondary info */}
            <div className="flex justify-between items-center p-3 bg-white/50 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-gray-700">{t('car.totalPrice')} / MSRP</h4>
                <p className="text-xs text-gray-500">{t('detailedAd.fixedPrice')}</p>
              </div>
              <div className="text-xl font-bold text-gray-900">
                ${car.totalPrice?.toLocaleString() ?? (car.monthlyPayment * car.termMonths).toLocaleString()}
              </div>
            </div>

            {/* Down Payment - when set */}
            {car.downPayment != null && car.downPayment > 0 && (
              <div className="flex justify-between items-center p-3 bg-white/50 rounded-lg">
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Down Payment</h4>
                  <p className="text-xs text-gray-500">Required at signing</p>
                </div>
                <div className="text-lg font-bold text-gray-900">
                  ${car.downPayment.toLocaleString()}
                </div>
              </div>
            )}
            
            {/* Credit Score Requirement */}
            <div className="flex items-center justify-center space-x-2 p-3 bg-green-50 rounded-lg border border-green-200">
              <Shield className="text-green-600" size={18} />
              <span className="text-sm text-green-700">
                {t('detailedAd.minCreditScore')}: <strong>{car.minCreditScore}</strong>
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Buy Now Button - Right under the payment card */}
      <Button 
        size="lg"
        onClick={() => onCarBuy(car)}
        className="w-full bg-[#f91bdc] hover:bg-[#e015c9] text-white text-lg py-3 shadow-lg hover:shadow-xl transition-all duration-200"
      >
        {t('common.buyNow')}
      </Button>
    </div>
  );
}

// Specifications card for below the main layout
function CarSpecifications({ car }: { car: CarType }) {
  const { t } = useLanguage();
  
  const features = [
    { icon: <Calendar size={20} />, label: t('car.year'), value: car.year.toString() },
    { icon: <Gauge size={20} />, label: t('car.mileage'), value: car.mileage ? `${car.mileage.toLocaleString()} ${t('vehicle.miles')}` : "N/A" },
    { icon: <Settings size={20} />, label: t('vehicle.transmission'), value: car.transmission || t('vehicle.automatic') },
    { icon: <Fuel size={20} />, label: t('vehicle.fuelType'), value: car.fuelType || t('vehicle.gasoline') },
    ...(car.vin ? [{ icon: <Shield size={20} />, label: 'VIN', value: car.vin }] : []),
  ];

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t('detailedAd.specifications')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${car.vin ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
          {features.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50">
              <div className="text-[#f91bdc] flex-shrink-0 mt-1">
                {feature.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-600 mb-1">{feature.label}</p>
                <p className="font-semibold text-sm break-words">{feature.value}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Shariah Compliant card for below the main layout
function ShariahCompliantInfo() {
  const { t } = useLanguage();

  return (
    <Card className="border-[#f91bdc]/20 w-full">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <IslamicSymbol size={24} className="text-[#f91bdc]" />
          <span>{t('detailedAd.shariahCompliant')}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="text-green-500 mt-1" size={20} />
            <div>
              <h4 className="font-semibold">{t('howItWorksPage.noInterestPrinciple')}</h4>
              <p className="text-gray-600 text-sm">{t('howItWorksPage.noInterestDescription')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="text-green-500 mt-1" size={20} />
            <div>
              <h4 className="font-semibold">{t('howItWorksPage.fixedTerms')}</h4>
              <p className="text-gray-600 text-sm">{t('howItWorksPage.fixedTermsDescription')}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <CheckCircle className="text-green-500 mt-1" size={20} />
            <div>
              <h4 className="font-semibold">{t('detailedAd.ethicalStandards')}</h4>
              <p className="text-gray-600 text-sm">{t('howItWorksPage.certifiedDescription')}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}



export default function DetailedAd({
  car,
  onBackToListings,
  onCarBuy,
  onContactClick,
  onOpenTerms,
  onOpenPrivacy
}: {
  car: CarType;
  onNavigate: (page: string) => void;
  onBackToListings: () => void;
  onCarBuy: (car: CarType) => void;
  onContactClick: () => void;
  onOpenTerms: () => void;
  onOpenPrivacy: () => void;
}) {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar 
        onContactClick={onContactClick} 
      />
      
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            variant="ghost" 
            onClick={onBackToListings}
            className="text-[#f91bdc] hover:text-[#e015c9] hover:bg-[#f91bdc]/10"
          >
            <ArrowLeft size={20} className="mr-2" />
            {t('common.backToListings')}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <style>{`
          @media (min-width: 1024px) {
            .detail-layout-grid {
              grid-template-columns: auto 1fr !important;
              align-items: start;
            }
          }
        `}</style>
        <div className="lg:grid lg:gap-12 detail-layout-grid">
          <div className="mb-8 lg:mb-0">
            <ImageGallery images={car.images} />
          </div>

          <div>
            <CarBasicInfo car={car} onCarBuy={onCarBuy} />
          </div>
        </div>

        {/* Description section - Only show if description exists */}
        {car.description && (
          <div className="mt-12">
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-2xl text-[#f91bdc] flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {car.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Additional requirements - when set */}
        {car.additionalRequirements && (
          <div className="mt-8">
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-xl text-[#f91bdc]">Additional Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {car.additionalRequirements}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Specifications and Shariah Compliant sections - Full width spanning carousel to payment card */}
        <div className="mt-12 space-y-8 lg:grid lg:grid-cols-[880px_1fr] lg:gap-12 lg:items-start">
          <div className="lg:col-span-2 lg:w-full">
            <CarSpecifications car={car} />
          </div>
          <div className="lg:col-span-2 lg:w-full">
            <ShariahCompliantInfo />
          </div>
        </div>

        <div className="mt-12">
          <Card className="border-[#f91bdc]/20">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => onCarBuy(car)}
                  className="flex-1 bg-[#f91bdc] hover:bg-[#e015c9] text-white text-lg py-3"
                >
                  {t('common.buyNow')}
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={onContactClick}
                  className="flex-1 border-[#f91bdc] text-[#f91bdc] hover:bg-[#f91bdc] hover:text-white text-lg py-3"
                >
                  {t('detailedAd.contactDealer')}
                </Button>
              </div>
              
              <div className="mt-6 p-4 bg-[#f91bdc]/5 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="text-[#f91bdc]" size={20} />
                  <span className="font-semibold text-gray-900">{t('detailedAd.purchaseProtection')}</span>
                </div>
                <p className="text-sm text-gray-600">
                  {t('detailedAd.purchaseProtectionDesc')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer 
        onOpenTerms={onOpenTerms} 
        onOpenPrivacy={onOpenPrivacy} 
      />
    </div>
  );
}